import { Location } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppSharedModule } from 'app/app-shared.module';
import { TimeoutEnum } from 'app/core/enum/timeout.enum';
import { AgrirouterService } from 'app/core/services/agrirouter.service';
import { DeviceService } from 'app/core/services/device.service';
import { DeviceDTO } from 'app/modules/gestao-operacao/fleet-monitoring/device/device.model';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Subject } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DatePipe, KeyValuePipe, NgFor } from '@angular/common';
import { AuthStateService } from 'app/core/services/auth-state.service';
import { AppConstants } from 'app/app.constants';
import { FileManagerService } from 'app/core/services/file-manager.service';
import { normalizeDeviceModel } from 'app/utils/device-model.utils';

const EQUIPMENT_LABEL_KEY_PREFIX = 'tasks.equipment.';

/** Device models that have a `tasks.equipment.*` label. */
const LABELED_DEVICE_MODELS = new Set<string>(
  Object.values(AppConstants.DEVICE_MODEL).map((model) => model.name),
);

/**
 * Device types that can't be onboarded as Agrirouter VCUs: UHF modules are
 * radio links, not machines, so they have no task data to exchange.
 * IsoView/Geonave can be onboarded to send the TaskData generated from their
 * NVG files.
 */
const DEVICE_TYPES_WITHOUT_AGRIROUTER = ['uhf-module'];

@Component({
  selector: 'app-agrirouter-onboard',
  standalone: true,
  imports: [AppSharedModule, MatFormFieldModule, KeyValuePipe, NgFor, DatePipe],
  templateUrl: './agrirouter-onboard.component.html',
  styleUrl: './agrirouter-onboard.component.scss',
})
export class AgrirouterOnboardComponent implements OnInit {
  error: string | null = null;
  signature: string | null = null;
  token: string | null = null;
  state: string | null = null;
  devices: Partial<any>[] = [];
  devicesToOnboard: Partial<any>[] = [];
  userHasOnboarded = false;
  showModalSubject: Subject<boolean> = new Subject();
  showModal: boolean;
  accountId: string;
  endpointId: string;
  certificateExpiresAt: string;
  files: any;
  private readonly authState = inject(AuthStateService);
  userId: string;

  constructor(
    private agrirouterService: AgrirouterService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private deviceService: DeviceService,
    private fileManagerService: FileManagerService,
    private location: Location,
  ) {}

  goBack(): void {
    this.location.back();
  }

  get currentLocale(): string {
    return this.translate.currentLang || AppConstants.LANGUAGES.PT_BR;
  }

  ngOnInit(): void {
    const url = new URL(window.location.href);
    const urlSearchParams = new URLSearchParams(url.search);

    this.error = urlSearchParams.get('error');
    this.signature = urlSearchParams.get('signature');
    this.state = urlSearchParams.get('state');
    this.token = urlSearchParams.get('token');

    if (this.signature && this.state && this.token) {
      this.agrirouterService
        .onboard(this.signature, this.state, this.token)
        .subscribe({
          next: (_response) => {
            this.feedBackToUser('success', 'agrirouter.onboard.success');
            this.checkOnboardAccountStatus();
            this.getAllDevicesConnectedToAgrirouter();
          },
          error: (error) => {
            if (error?.error?.code === 'ACCOUNT_MISMATCH') {
              this.feedBackToUser(
                'error',
                'agrirouter.onboard.account-mismatch',
              );
              this.checkOnboardAccountStatus();
              this.getAllDevicesConnectedToAgrirouter();
            } else {
              this.feedBackToUser('error', 'agrirouter.onboard.error');
            }
          },
        });
    } else {
      if (this.error && this.error === 'request_declined') {
        this.translate
          .get('agrirouter.start-onboard.user-rejected')
          .subscribe((msg) => {
            this.toastr.error(msg, '', {
              positionClass: 'toast-top-full-width',
              timeOut: TimeoutEnum.MediumLong,
            });
          });
      }
      this.pingEndpointStatus();
      this.checkOnboardAccountStatus();
      this.getAllDevicesConnectedToAgrirouter();
      this.getAgrirouterFiles();
      const usuarioLogado = this.authState.getUser();
      this.userId = usuarioLogado?.id;
    }

    const newUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }

  checkOnboardAccountStatus() {
    this.agrirouterService.checkOnboardStatus().subscribe({
      next: (response) => {
        if (response.onboarded) {
          this.userHasOnboarded = true;
          this.accountId = response.data.accountId;
          this.endpointId = response.data.endpointId;
          this.certificateExpiresAt = response.data.certificateExpiresAt;
        }
      },
    });
  }

  pingEndpointStatus() {
    this.agrirouterService.pingEndpoint().subscribe({
      next: (response) => {
        if (response.deleted) {
          this.userHasOnboarded = false;
          this.devices = [];
          this.feedBackToUser('info', 'agrirouter.ping.endpoint-deleted');
        }
      },
      error: (err: HttpErrorResponse) => {
        // Backend returns 404 when the user has no Agrirouter onboarding yet
        // — an expected "not connected" state, not an actionable error. The
        // service also suppresses the interceptor's generic 404 toast for
        // this same reason (see pingEndpoint()'s INTERCEPTNotFound override).
        if (err.status !== HttpStatusCode.NotFound) {
          this.feedBackToUser('error', 'agrirouter.ping.error');
        }
      },
    });
  }

  getAllDevicesConnectedToAgrirouter() {
    this.agrirouterService.getAllDevicesConnectedToAgrirouter().subscribe({
      next: (response) => {
        this.devices = response.devices;
        this.changeDeviceStatus();
      },
      error: (error) => {
        this.changeDeviceStatus();
      },
    });
  }

  triggerAgrirouterIntegration() {
    this.agrirouterService
      .getOnboardAuthObject(this.agrirouterService.agrirouterOnboardUrl)
      .subscribe({
        next: (response) => {
          window.open(response.authorizationUrl, '_self');
        },
        error: (error) => {
          // this.toastr.error('ERR ERR ERR');
        },
      });
  }

  private async changeDeviceStatus(): Promise<void> {
    let devicesList = await this.getAllDevices();
    this.devicesToOnboard = [];
    const devicesOnboarded = new Set(this.devices.map((item) => item.deviceId));
    devicesList = devicesList.filter(
      (item) => !DEVICE_TYPES_WITHOUT_AGRIROUTER.includes(item.deviceType),
    );
    devicesList.forEach((item) => {
      item.onboarded = devicesOnboarded.has(item.id);
      item.labelKey = this.getDeviceLabelKey(item.deviceType);
    });
    this.devicesToOnboard.push(...devicesList);
  }

  /**
   * Translation key of the equipment name shown on the device card. Device
   * types without a label (legacy or empty) keep the former agroNave label.
   */
  getDeviceLabelKey(deviceType: string): string {
    const model = normalizeDeviceModel(deviceType);
    if (LABELED_DEVICE_MODELS.has(model)) {
      return EQUIPMENT_LABEL_KEY_PREFIX + model;
    }
    return EQUIPMENT_LABEL_KEY_PREFIX + AppConstants.DEVICE_MODEL.AGRONAVE.name;
  }

  private getAllDevices(): Promise<Partial<any>[]> {
    return lastValueFrom(this.deviceService.getAllDevices());
  }

  onboardVCU(device: Partial<any>): void {
    this.feedBackToUser('info', 'agrirouter.onboard.try-onboard-device');

    this.agrirouterService.onboardVCU(device.id).subscribe({
      next: (_response) => {
        this.feedBackToUser('success', 'agrirouter.onboard.vcu-success');
        this.getAllDevicesConnectedToAgrirouter();
      },
      error: (_error) => {
        this.feedBackToUser('error', 'agrirouter.onboard.vcu-error');
      },
    });
  }

  offboardVCU(device: Partial<any>): void {
    this.feedBackToUser('info', 'agrirouter.offboard.try-offboard-device');
    this.agrirouterService.offBoardVCU(device.id).subscribe({
      next: (_response) => {
        this.getAllDevicesConnectedToAgrirouter();
        this.feedBackToUser('success', 'agrirouter.offboard.vcu-success');
      },
      error: (_error) => {
        this.feedBackToUser('error', 'agrirouter.offboard.vcu-error');
      },
    });
  }

  offboardAccount(): void {
    this.feedBackToUser('info', 'agrirouter.offboard.try-offboard-account');
    this.agrirouterService.offBoardUser().subscribe({
      next: (_response) => {
        this.feedBackToUser('success', 'agrirouter.offboard.account-success');
        this.userHasOnboarded = false;
        this.getAllDevicesConnectedToAgrirouter();
      },
      error: (_error) => {
        this.feedBackToUser('error', 'agrirouter.offboard.account-error');
      },
    });
  }

  addDevices() {
    this.showModal = true;
    this.showModalSubject.next(true);
  }

  close() {
    this.showModal = false;
    this.showModalSubject.next(false);
  }

  feedBackToUser(type: string, msg: string) {
    const message = this.translate.instant(msg);
    switch (type) {
      case 'info':
        this.toastr.info(message, '', {
          positionClass: 'toast-top-full-width',
          timeOut: TimeoutEnum.MediumLong,
        });
        break;
      case 'success':
        this.toastr.success(message, '', {
          positionClass: 'toast-top-full-width',
          timeOut: TimeoutEnum.MediumLong,
        });
        break;
      case 'error':
        this.toastr.error(message, '', {
          positionClass: 'toast-top-full-width',
          timeOut: TimeoutEnum.MediumLong,
        });
        break;
    }
  }

  getShapefiles() {
    this.feedBackToUser('info', 'agrirouter.messages.importing-files');
    this.agrirouterService.getShapefiles().subscribe({
      next: (_response) => {
        this.feedBackToUser(
          'success',
          'agrirouter.messages.importing-files-success',
        );
        this.getAgrirouterFiles();
      },
      error: (_error) => {
        this.feedBackToUser(
          'error',
          'agrirouter.messages.importing-files-error',
        );
      },
    });
  }

  getAgrirouterFiles() {
    this.agrirouterService.getAgrirouterFiles().subscribe({
      next: (response) => {
        this.files = response.files;
      },
      error: (_error) => {
        this.feedBackToUser('error', 'agrirouter.messages.get-files-error');
      },
    });
  }

  downloadFile(deviceId: string, fileName: string) {
    const filesWithFolder = [
      `${this.userId}/agrirouter/${deviceId}/${fileName}`,
    ];

    this.fileManagerService
      .signedUrl(filesWithFolder)
      .subscribe((sasUrls: Array<string>) => {
        sasUrls.forEach((url, index) => {
          this.fileManagerService.downloadFile(url, fileName);
        });
      });
  }

  deleteFile(deviceId: string, fileName: string) {
    this.fileManagerService
      .deleteFile('user-files', deviceId, fileName)
      .subscribe(() => {
        this.getAgrirouterFiles();
      });
  }
}
